import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Alert, Form } from 'react-bootstrap';
import moment from 'moment';
import {
  SHARE_ACCESS_TYPE_LABELS,
  SHARE_ACCESS_TYPE_OPTIONS,
  SHARE_ACCESS_TYPES
} from 'util/shareRequestAccess';

const formatDisplayDate = dateStr => {
  if (!dateStr) return null;
  const m = moment(dateStr, 'YYYY-MM-DD', true);
  return m.isValid() ? m.format('MMMM D, YYYY') : dateStr;
};

const getTimeframeAlertMessage = (scopeFromDate, scopeToDate) => {
  const from = formatDisplayDate(scopeFromDate);
  const to = formatDisplayDate(scopeToDate);

  if (from && to) {
    return `Journal entries created from ${from} through ${to} will be shared.`;
  }
  if (from) {
    return `Journal entries created after ${from} will be shared.`;
  }
  if (to) {
    return `Journal entries created before ${to} will be shared.`;
  }
  return '';
};

const openDatePicker = event => {
  const input = event.currentTarget;
  if (input.disabled || typeof input.showPicker !== 'function') {
    return;
  }
  try {
    input.showPicker();
  } catch {
    // showPicker can throw if the browser blocks it outside a user gesture
  }
};

const ShareRequestAccessFields = ({
  control,
  errors,
  disabled,
  accessTypeId
}) => {
  const idPrefix = accessTypeId || 'share-request-access';
  const currentAccessType = useWatch({ control, name: 'accessType' });
  const { setValue } = useFormContext();
  const scopeFromDate = useWatch({ control, name: 'scopeFromDate' });
  const scopeToDate = useWatch({ control, name: 'scopeToDate' });
  const scopeFromError = errors?.scopeFromDate?.message;
  const scopeToError = errors?.scopeToDate?.message;

  const handleStartDateChange = (field, event) => {
    const value = event.target.value;
    field.onChange(value);
    if (value && scopeToDate && value > scopeToDate) {
      setValue('scopeToDate', '', { shouldValidate: true });
    }
  };

  const handleEndDateChange = (field, event) => {
    const value = event.target.value;
    field.onChange(value);
    if (value && scopeFromDate && value < scopeFromDate) {
      setValue('scopeFromDate', '', { shouldValidate: true });
    }
  };

  const timeframeAlertMessage = useMemo(
    () => getTimeframeAlertMessage(scopeFromDate, scopeToDate),
    [scopeFromDate, scopeToDate]
  );

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Sharing access</Form.Label>
        <Controller
          name="accessType"
          control={control}
          render={({ field }) => (
            <Form.Select {...field} disabled={disabled}>
              {SHARE_ACCESS_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          )}
        />
      </Form.Group>

      {currentAccessType === SHARE_ACCESS_TYPES.FULL && (
        <Alert variant="info" className="fs--1 mb-3">
          <Alert.Heading as="h6" className="fs--1 mb-2 fw-bold">
            {SHARE_ACCESS_TYPE_LABELS[SHARE_ACCESS_TYPES.FULL]}
          </Alert.Heading>
          All journal entries will be shared.
        </Alert>
      )}

      {currentAccessType === SHARE_ACCESS_TYPES.SELECTIVE && (
        <Alert variant="info" className="fs--1 mb-3">
          <Alert.Heading as="h6" className="fs--1 mb-2 fw-bold">
            {SHARE_ACCESS_TYPE_LABELS[SHARE_ACCESS_TYPES.SELECTIVE]}
          </Alert.Heading>
          After the request is accepted, you can choose which journal entries to
          share manually.
        </Alert>
      )}

      {currentAccessType === SHARE_ACCESS_TYPES.TIMEFRAME && (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Start date</Form.Label>
            <Controller
              name="scopeFromDate"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="date"
                  id={`${idPrefix}-scope-from`}
                  disabled={disabled}
                  isInvalid={!!scopeFromError}
                  max={scopeToDate || undefined}
                  className="cursor-pointer"
                  onClick={openDatePicker}
                  onChange={e => handleStartDateChange(field, e)}
                />
              )}
            />
            {scopeFromError ? (
              <div className="text-danger fs--1 mt-1">{scopeFromError}</div>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>End date</Form.Label>
            <Controller
              name="scopeToDate"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="date"
                  id={`${idPrefix}-scope-to`}
                  disabled={disabled}
                  isInvalid={!!scopeToError}
                  min={scopeFromDate || undefined}
                  className="cursor-pointer"
                  onClick={openDatePicker}
                  onChange={e => handleEndDateChange(field, e)}
                />
              )}
            />
            {scopeToError ? (
              <div className="text-danger fs--1 mt-1">{scopeToError}</div>
            ) : null}
          </Form.Group>
          {timeframeAlertMessage && (
            <Alert variant="info" className="fs--1 mb-0">
              {timeframeAlertMessage}
            </Alert>
          )}
        </>
      )}
    </>
  );
};

ShareRequestAccessFields.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  disabled: PropTypes.bool,
  accessTypeId: PropTypes.string
};

ShareRequestAccessFields.defaultProps = {
  errors: {},
  disabled: false,
  accessTypeId: ''
};

export default ShareRequestAccessFields;
